import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';

const navLinks = [
  {
    label: 'Products',
    dropdown: [
      { label: 'BriefingIQ', desc: 'Executive Briefing Management', to: '/briefingiq' },
      { label: 'EventIQ', desc: 'Corporate Event Management', to: '/eventiq' },
    ],
  },
  { label: 'Platform', to: '/platform' },
  { label: 'Company', to: '/company' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Blog', to: '/blog' },
  { label: 'Contact', to: '/contact' },
];

const Header = ({ onOpenDemo }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/85 backdrop-blur-xl border-b border-[var(--line-2)]'
          : 'bg-white/70 backdrop-blur-lg border-b border-transparent'
      }`}
    >
      <nav aria-label="Primary" className="max-w-6xl mx-auto px-6 h-12 flex items-center justify-between">
        <Link to="/" className="font-semibold text-[15px] tracking-tight text-ink inline-flex items-center gap-2">
          BriefingIQ
          <span className="text-[9px] font-semibold tracking-[0.15em] uppercase bg-[#4F46E5] text-white rounded-full px-1.5 py-0.5">AI</span>
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((item) =>
            item.dropdown ? (
              <div key={item.label} className="relative group">
                <button className="text-[13px] font-normal text-ink-2 hover:text-ink transition-colors inline-flex items-center gap-1">
                  {item.label}
                  <ChevronDown className="w-3 h-3" />
                </button>
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="bg-white shadow-2xl rounded-2xl border border-[var(--line-2)] p-2 min-w-[280px]">
                    {item.dropdown.map((d) => (
                      <Link
                        key={d.label}
                        to={d.to}
                        className="block px-4 py-3 rounded-xl hover:bg-[var(--bg-alt)] transition-colors"
                      >
                        <div className="font-medium text-ink text-sm">{d.label}</div>
                        <div className="text-xs text-ink-3 mt-0.5">{d.desc}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={item.label}
                to={item.to}
                className={({ isActive }) =>
                  `text-[13px] transition-colors ${
                    isActive ? 'text-ink font-medium' : 'text-ink-2 hover:text-ink'
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </div>

        <div className="hidden lg:flex items-center gap-6">
          <a
            href="#login"
            className="text-[13px] font-normal text-ink-2 hover:text-ink transition-colors"
          >
            Sign in
          </a>
          <button
            onClick={onOpenDemo}
            className="btn-primary !py-2 !px-4 text-[13px]"
          >
            Request demo
          </button>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden p-2 text-ink"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </nav>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-[var(--line-2)] px-6 py-4 space-y-1">
          {navLinks.flatMap((item) => {
            if (item.dropdown) {
              return [
                <div key={item.label} className="text-[11px] uppercase tracking-widest text-ink-3 mt-2 mb-1">
                  {item.label}
                </div>,
                ...item.dropdown.map((d) => (
                  <Link key={d.to} to={d.to} className="block py-2 text-sm text-ink">
                    {d.label}
                  </Link>
                )),
              ];
            }
            return (
              <Link key={item.to} to={item.to} className="block py-2.5 text-sm text-ink">
                {item.label}
              </Link>
            );
          })}
          <button onClick={onOpenDemo} className="btn-primary mt-3 w-full justify-center">
            Request demo
          </button>
        </div>
      )}
    </header>
  );
};

export default Header;
